import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface ThemeColors {
  primary: string;
  primaryLight: string;
  primaryDark: string;
  primaryTransparent: string;
  text: string;
  background: string;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private defaultColors: ThemeColors = {
    primary: '#1890ff',
    primaryLight: '#40a9ff',
    primaryDark: '#096dd9',
    primaryTransparent: 'rgba(24, 144, 255, 0.1)',
    text: '#ffffff',
    background: '#001529'
  };

  private currentTheme$ = new BehaviorSubject<ThemeColors>(this.defaultColors);

  constructor() {
    // Initialize with saved theme or default
    const savedTheme = this.getSavedTheme();
    if (savedTheme) {
      this.applyTheme(savedTheme);
    }
  }

  /**
   * Get current theme observable
   */
  getTheme(): Observable<ThemeColors> {
    return this.currentTheme$.asObservable();
  }

  /**
   * Get current theme value
   */
  getCurrentTheme(): ThemeColors {
    return this.currentTheme$.value;
  }

  /**
   * Set theme from team color
   */
  setTeamTheme(teamColor: string | undefined, teamName?: string): void {
    if (!teamColor) {
      this.resetToDefault();
      return;
    }

    const colors = this.generateThemeFromColor(teamColor);
    this.applyTheme(colors);
    this.saveTheme(colors, teamName);
  }

  /**
   * Reset to default theme
   */
  resetToDefault(): void {
    this.applyTheme(this.defaultColors);
    this.clearSavedTheme();
  }

  /**
   * Generate theme colors from a base color
   */
  private generateThemeFromColor(baseColor: string): ThemeColors {
    // Convert hex to RGB for manipulation
    const rgb = this.hexToRgb(baseColor);
    if (!rgb) {
      return this.defaultColors;
    }

    // Generate lighter version (for hover states)
    const lighter = this.adjustBrightness(rgb, 20);
    const lighterHex = this.rgbToHex(lighter.r, lighter.g, lighter.b);

    // Generate darker version (for active states)
    const darker = this.adjustBrightness(rgb, -20);
    const darkerHex = this.rgbToHex(darker.r, darker.g, darker.b);

    // Generate transparent version
    const transparent = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1)`;

    // Determine text color based on brightness
    const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
    const textColor = brightness > 128 ? '#000000' : '#ffffff';

    // Generate background color (darker version for sidebar)
    const bgDarker = this.adjustBrightness(rgb, -60);
    const backgroundHex = this.rgbToHex(bgDarker.r, bgDarker.g, bgDarker.b);

    return {
      primary: baseColor,
      primaryLight: lighterHex,
      primaryDark: darkerHex,
      primaryTransparent: transparent,
      text: textColor,
      background: backgroundHex
    };
  }

  /**
   * Apply theme to DOM
   */
  private applyTheme(colors: ThemeColors): void {
    const root = document.documentElement;

    // Set CSS variables
    root.style.setProperty('--primary-color', colors.primary);
    root.style.setProperty('--primary-color-light', colors.primaryLight);
    root.style.setProperty('--primary-color-dark', colors.primaryDark);
    root.style.setProperty('--primary-color-transparent', colors.primaryTransparent);
    root.style.setProperty('--primary-text-color', colors.text);
    root.style.setProperty('--sidebar-background', colors.background);

    // Apply to Ant Design theme
    this.updateAntDesignTheme(colors);

    // Emit new theme
    this.currentTheme$.next(colors);
  }

  /**
   * Update Ant Design component theme
   */
  private updateAntDesignTheme(colors: ThemeColors): void {
    // Update Ant Design CSS variables
    const root = document.documentElement;

    // Primary color variations
    root.style.setProperty('--ant-primary-color', colors.primary);
    root.style.setProperty('--ant-primary-color-hover', colors.primaryLight);
    root.style.setProperty('--ant-primary-color-active', colors.primaryDark);
    root.style.setProperty('--ant-primary-color-outline', colors.primaryTransparent);

    // Update specific Ant components
    root.style.setProperty('--ant-primary-1', colors.primaryTransparent);
    root.style.setProperty('--ant-primary-2', this.adjustOpacity(colors.primary, 0.2));
    root.style.setProperty('--ant-primary-3', this.adjustOpacity(colors.primary, 0.3));
    root.style.setProperty('--ant-primary-4', this.adjustOpacity(colors.primary, 0.4));
    root.style.setProperty('--ant-primary-5', this.adjustOpacity(colors.primary, 0.5));
    root.style.setProperty('--ant-primary-6', colors.primary);
    root.style.setProperty('--ant-primary-7', colors.primaryDark);

    // Menu theme
    root.style.setProperty('--ant-menu-dark-bg', colors.background);
    root.style.setProperty('--ant-menu-dark-inline-submenu-bg', this.adjustOpacity(colors.primary, 0.1));
    root.style.setProperty('--ant-menu-dark-item-active-bg', colors.primary);
    root.style.setProperty('--ant-menu-dark-item-selected-bg', colors.primary);

    // Button theme
    root.style.setProperty('--ant-btn-primary-bg', colors.primary);
    root.style.setProperty('--ant-btn-primary-hover-bg', colors.primaryLight);

    // Link color
    root.style.setProperty('--ant-link-color', colors.primary);
    root.style.setProperty('--ant-link-hover-color', colors.primaryLight);
  }

  /**
   * Save theme to localStorage
   */
  private saveTheme(colors: ThemeColors, teamName?: string): void {
    const themeData = {
      colors,
      teamName,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('team-theme', JSON.stringify(themeData));
  }

  /**
   * Get saved theme from localStorage
   */
  private getSavedTheme(): ThemeColors | null {
    const saved = localStorage.getItem('team-theme');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        return data.colors;
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Clear saved theme
   */
  private clearSavedTheme(): void {
    localStorage.removeItem('team-theme');
  }

  /**
   * Utility: Convert hex to RGB
   */
  private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  /**
   * Utility: Convert RGB to hex
   */
  private rgbToHex(r: number, g: number, b: number): string {
    return '#' + [r, g, b].map(x => {
      const hex = Math.max(0, Math.min(255, Math.round(x))).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('');
  }

  /**
   * Utility: Adjust brightness
   */
  private adjustBrightness(rgb: { r: number; g: number; b: number }, percent: number): { r: number; g: number; b: number } {
    return {
      r: Math.max(0, Math.min(255, rgb.r + (255 * percent / 100))),
      g: Math.max(0, Math.min(255, rgb.g + (255 * percent / 100))),
      b: Math.max(0, Math.min(255, rgb.b + (255 * percent / 100)))
    };
  }

  /**
   * Utility: Adjust opacity
   */
  private adjustOpacity(hex: string, opacity: number): string {
    const rgb = this.hexToRgb(hex);
    if (!rgb) return hex;
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
  }
}